import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "react-query";
import { PlusIcon, MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import { getClient, getClients } from "../functions/api_fethcers";

/**
 * Searchable async client picker.
 *
 * Replaces the legacy ClientDropdown, which received a pre-fetched (and
 * silently page-capped) list. This component queries `/clients?search=...`
 * on each keystroke (debounced) so every client is reachable regardless of
 * how many exist. The currently selected client is fetched by id so its
 * display name shows correctly in edit mode even when it falls outside the
 * current search results.
 */

const RESULT_LIMIT = 20;
const DEBOUNCE_MS = 200;

const ClientSearchSelect = ({
  selectedClientId,
  onClientSelect,
  onAddNew,
  placeholder = "Search clients by name or contact...",
}) => {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Debounce the query passed to react-query so we don't fire on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  // Search results. With an empty query the backend returns the first page,
  // which gives a sensible "recent clients" list when the user just opens the menu.
  const {
    data: searchData,
    isLoading: isSearching,
    isError: isSearchError,
  } = useQuery(
    ["clients", "search", debouncedQuery],
    () =>
      getClients({
        page: 0,
        limit: RESULT_LIMIT,
        filters: { search: debouncedQuery },
      }),
    {
      enabled: isOpen,
      keepPreviousData: true,
      staleTime: 60_000,
    }
  );

  // Resolve the currently selected client (for display label in the input
  // and to bubble the full object back up on hydration). Cached per id.
  const { data: selectedClient } = useQuery(
    ["client", selectedClientId],
    () => getClient(selectedClientId),
    {
      enabled: !!selectedClientId,
      staleTime: 300_000,
    }
  );

  // When react-query resolves the selected client (e.g. on first edit-mode load),
  // forward it to the parent so dependent fields can autofill.
  useEffect(() => {
    if (selectedClient && selectedClientId) {
      onClientSelect(selectedClientId, selectedClient);
    }
    // We intentionally only react to the resolved client object identity,
    // not onClientSelect (which is a fresh closure each render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient]);

  // Close on outside click.
  useEffect(() => {
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const results = searchData?.items ?? [];
  const totalMatches = searchData?.total ?? 0;
  const hasMore = totalMatches > results.length;

  const handleSelect = (client) => {
    onClientSelect(client._id, client);
    setQuery("");
    setIsOpen(false);
    setActiveIndex(-1);
    inputRef.current?.blur();
  };

  const handleClear = () => {
    onClientSelect("", null);
    setQuery("");
    setActiveIndex(-1);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (!isOpen && (e.key === "ArrowDown" || e.key === "Enter")) {
      setIsOpen(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0 && results[activeIndex]) {
        handleSelect(results[activeIndex]);
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
      setActiveIndex(-1);
    }
  };

  // The input shows either the user's in-progress query, or the selected
  // client's name when they're not actively typing.
  const inputValue = isOpen
    ? query
    : selectedClient?.name ?? (selectedClientId ? "Loading..." : "");

  return (
    <div className="relative w-full" ref={containerRef}>
      <div className="relative">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-grayteck-400 dark:text-grayteck-500" />
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          placeholder={placeholder}
          onFocus={() => setIsOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
            setActiveIndex(-1);
          }}
          onKeyDown={handleKeyDown}
          className="w-full rounded-md border-grayteck-300 shadow-sm focus:border-grayteck-500 focus:ring-grayteck-500 dark:bg-darkbg-700 dark:border-darkbg-600 dark:text-grayteck-100 bg-gray-50 text-grayteck-900 p-2 pl-9 pr-20 text-sm sm:text-base"
        />
        {selectedClientId && !isOpen && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-grayteck-500 hover:text-grayteck-700 dark:text-grayteck-400 dark:hover:text-grayteck-200 px-2 py-1 rounded"
          >
            Clear
          </button>
        )}
      </div>

      {isOpen && (
        <div className="absolute z-20 mt-1 w-full rounded-md border border-grayteck-200 dark:border-darkbg-600 bg-white dark:bg-darkbg-800 shadow-lg max-h-72 overflow-y-auto">
          {isSearching && results.length === 0 && (
            <div className="p-3 text-sm text-grayteck-500 dark:text-grayteck-400">
              Searching...
            </div>
          )}

          {isSearchError && (
            <div className="p-3 text-sm text-red-600 dark:text-red-400">
              Failed to load clients. Try again.
            </div>
          )}

          {!isSearching && !isSearchError && results.length === 0 && (
            <div className="p-3 text-sm text-grayteck-500 dark:text-grayteck-400">
              {debouncedQuery
                ? `No clients match "${debouncedQuery}".`
                : "No clients found."}
            </div>
          )}

          {results.map((client, idx) => {
            const isActive = idx === activeIndex;
            const isSelected = client._id === selectedClientId;
            return (
              <button
                type="button"
                key={client._id}
                onMouseEnter={() => setActiveIndex(idx)}
                onClick={() => handleSelect(client)}
                className={[
                  "w-full text-left px-3 py-2 text-sm border-b border-grayteck-100 dark:border-darkbg-700 last:border-b-0 flex items-center justify-between gap-3",
                  isActive
                    ? "bg-grayteck-100 dark:bg-darkbg-700"
                    : "hover:bg-grayteck-50 dark:hover:bg-darkbg-700/60",
                  isSelected
                    ? "text-grayteck-900 dark:text-grayteck-100 font-medium"
                    : "text-grayteck-800 dark:text-grayteck-200",
                ].join(" ")}
              >
                <span className="truncate">
                  {client.name}
                  {client.client_type === "business" &&
                    client.contact_info?.primary_contact && (
                      <span className="ml-2 text-xs text-grayteck-500 dark:text-grayteck-400">
                        {client.contact_info.primary_contact}
                      </span>
                    )}
                </span>
                <span className="text-[10px] uppercase tracking-wide text-grayteck-400 dark:text-grayteck-500">
                  {client.status || client.client_type}
                </span>
              </button>
            );
          })}

          {hasMore && (
            <div className="px-3 py-2 text-xs text-grayteck-500 dark:text-grayteck-400 border-b border-grayteck-100 dark:border-darkbg-700">
              Showing {results.length} of {totalMatches}. Keep typing to narrow.
            </div>
          )}

          <button
            type="button"
            onClick={() => {
              setIsOpen(false);
              setQuery("");
              onAddNew?.();
            }}
            className="w-full text-left px-3 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-darkbg-700 flex items-center gap-2"
          >
            <PlusIcon className="h-4 w-4" />
            Add New Client...
          </button>
        </div>
      )}
    </div>
  );
};

export default ClientSearchSelect;
